import { getViewerType } from "lib/actions/assets/getViewerType";
import { useCurrentProjectId } from "../useProject";
import { useAssetsControls } from "lib/hooks/assets/useAssetsControls";
import { useCesium } from "components/elements/CesiumViewer/core/useCesium";
import { useAssets } from "lib/hooks/assets/useAssets";
import { useViewTools } from "lib/hooks/views/useViewTools";
import { useProjectLayers } from "lib/hooks/layers/useProjectLayers";
import { useLayersControls } from "lib/hooks/layers/useLayersControls";
import {
  useViewsMode,
  BeforeViewModeState,
} from "lib/contexts/SpatialContexts/ViewsModeContext";
import { KeyframeViewer } from "lib/contexts/ProjectContexts/SelectedViewerContext";
import { ViewModel } from "lib/types/views/ViewModel";

export function useViewActions() {
  const projectId = useCurrentProjectId();
  const { showAsset } = useAssetsControls();
  const { cameraPlugin, assetsPlugin, toolManager } = useCesium();
  const { versions } = useAssets(projectId);
  const { layers } = useProjectLayers(projectId);
  const { showLayerAssets } = useLayersControls();
  const {
    loadedView,
    setLoadedView,
    beforeViewModeState,
    setBeforeViewModeState,
    exitViewMode,
    setCameraMoved,
  } = useViewsMode();
  const { restoreToolsState, captureToolsState } = useViewTools();

  function clearViewer() {
    assetsPlugin.hideAll();
    toolManager.clearAllBackgroundTools();
  }

  async function showViewAssets(view: ViewModel) {
    if (!versions) return;
    const viewVersionIds = new Set(view.assetVersionIds);
    const versionsToShow = versions.filter(
      (v) =>
        viewVersionIds.has(v.id) &&
        getViewerType(v.contentUri) === KeyframeViewer.Cesium,
    );
    await Promise.all(versionsToShow.map((v) => showAsset(v)));
  }

  function flyToView(view: ViewModel) {
    if (view.cameraPosition?.length) {
      return cameraPlugin.flyToCameraTransform(view.cameraPosition);
    }
  }

  async function captureState(): Promise<BeforeViewModeState> {
    const assetSnapshot = assetsPlugin.assets.getSnapshot();
    return {
      cameraTransform: await cameraPlugin.captureCameraPosition(),
      toolsState: captureToolsState(),
      visibleLayerIds: [...assetsPlugin.layers.getSnapshot()],
      visibleContentUris: [...assetSnapshot.values()]
        .filter((a) => a.isShowing && a.layerIds.length === 0)
        .map((a) => a.contentUri),
    };
  }

  async function restoreCapturedState(state: BeforeViewModeState) {
    if (state.cameraTransform) {
      cameraPlugin.flyToCameraTransform(state.cameraTransform);
    }
    restoreToolsState(state.toolsState);

    const layersToRestore = (layers ?? []).filter((l) =>
      (state.visibleLayerIds ?? []).includes(l.id),
    );
    const versionsToRestore =
      versions?.filter((v) =>
        state.visibleContentUris.includes(v.contentUri),
      ) ?? [];

    await Promise.all([
      ...layersToRestore.map((l) => showLayerAssets(l)),
      ...versionsToRestore.map((v) => showAsset(v)),
    ]);
  }

  async function loadView(view: ViewModel) {
    if (!loadedView) {
      const state = await captureState();
      setBeforeViewModeState(state);
    }
    clearViewer();

    await showViewAssets(view);
    flyToView(view);
    restoreToolsState(view.toolsStateJson);
    setLoadedView(view);
    setCameraMoved(false);
  }

  function restoreView() {
    if (!loadedView) return;
    loadView(loadedView);
  }

  async function exitView() {
    if (!loadedView) return;
    clearViewer();
    if (beforeViewModeState) {
      await restoreCapturedState(beforeViewModeState);
    }
    exitViewMode();
  }

  function exitViewIfLoaded(view: ViewModel) {
    if (loadedView?.id === view.id) exitView();
  }

  return {
    loadView,
    restoreView,
    exitView,
    exitViewIfLoaded,
  };
}
